import { UNLIMITED_AI_CREDITS_SENTINEL } from "@hex/common";
import React from "react";
import styled from "styled-components";

import { HexCleanLink, Txt } from "../../hex-components";
import { useCurrentUserIsAdmin } from "../../hooks/me/useCurrentUserIsAdmin";
import { Routes } from "../../route/routes";
import { ContactAnAdmin } from "../common/ContactAnAdmin";

interface CreditExplanationProps {
  enforcementDate: Date;
  topUpsEnabled: boolean;
  workspaceCreditsAvailable: boolean;
  userCreditsTotal: number | typeof UNLIMITED_AI_CREDITS_SENTINEL;
}

export const CreditExplanation = React.memo(function CreditExplanation({
  enforcementDate,
  topUpsEnabled,
  userCreditsTotal,
  workspaceCreditsAvailable,
}: CreditExplanationProps) {
  const isAdmin = useCurrentUserIsAdmin();
  const isPreEnforcement = new Date() < enforcementDate;

  const enforcementDateString = enforcementDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const userCreditsTotalString =
    userCreditsTotal === UNLIMITED_AI_CREDITS_SENTINEL
      ? "unlimited"
      : userCreditsTotal;

  if (isPreEnforcement) {
    if (topUpsEnabled || workspaceCreditsAvailable) {
      return (
        <div>
          Credit grants go into effect on{" "}
          <Txt fontWeight="medium">{enforcementDateString}</Txt>. Your seat
          includes <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free
          credits per month, and you have access to add-on credits.{" "}
          {isAdmin && (
            <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
              Manage credits
            </InlineLink>
          )}
        </div>
      );
    }

    // Else if top-ups are disabled and theres no credits available
    return isAdmin ? (
      <div>
        Credit grants go into effect on{" "}
        <Txt fontWeight="medium">{enforcementDateString}</Txt>. Your seat
        includes <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free
        credits per month. Enable add-on credits in{" "}
        <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
          Billing & credits
        </InlineLink>
        .
      </div>
    ) : (
      <div>
        Credit grants go into effect on{" "}
        <Txt fontWeight="medium">{enforcementDateString}</Txt>. Your seat
        includes <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free
        credits per month. <ContactAnAdmin /> to request add-on credits.
      </div>
    );
  }

  // TODO: Implement post-enforcement explanation.
  // No orgs are post-enforcement yet, so we don't need to implement this yet.
  return null;
});

const InlineLink = styled(HexCleanLink)`
  && {
    color: ${({ theme }) => theme.fontColor.LINK};
  }
`;
